import { debounce } from "./utils-DjFceY3H.js";
import { Search, X } from "./icons-DDR9dFSr.js";
import { InputGroup } from "./InputGroup-BWeanEo7.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
// src/blocks/SearchInput/SearchInput.tsx
/**
* Debounced search input with a search icon and optional clear button.
*
* - Use for list / table search and free-text filters, e.g. in `PageToolbar.Search`.
* - Not for plain text (`TextInput`) or picking from a known list (`Combobox`).
*
* @see RECIPES.md#filter-toolbar
*/
const SearchInput = ({ ref, state, focusVariant, className, value: valueProp, defaultValue, onValueChange, onValueBlur, onChange, onBlur, onClear, allowClear = false, disableDebounce = false, debounceDelay = 300, disabled, readOnly, ...restInputProps }) => {
	const [internalValue, setInternalValue] = useState(valueProp != null ? valueProp : defaultValue ?? "");
	const debounceInFlightRef = useRef(false);
	const cancelRef = useRef(() => {});
	const debouncedOnValueChange = useMemo(() => {
		if (!onValueChange || disableDebounce) {
			cancelRef.current = () => {};
			return onValueChange;
		}
		const d = debounce((val) => {
			debounceInFlightRef.current = false;
			onValueChange(val);
		}, debounceDelay);
		cancelRef.current = d.cancel;
		return d;
	}, [
		onValueChange,
		disableDebounce,
		debounceDelay
	]);
	useEffect(() => {
		return () => cancelRef.current();
	}, [debouncedOnValueChange]);
	useEffect(() => {
		if (valueProp == null) return;
		if (debounceInFlightRef.current) return;
		setInternalValue(valueProp);
	}, [valueProp]);
	const canClear = allowClear && !!internalValue && !disabled && !readOnly;
	const handleChange = (event) => {
		const val = event.target.value;
		setInternalValue(val);
		if (!disableDebounce && debouncedOnValueChange) debounceInFlightRef.current = true;
		debouncedOnValueChange?.(val);
		onChange?.(event);
	};
	const handleBlur = (event) => {
		onValueBlur?.(event.target.value);
		onBlur?.(event);
	};
	const handleClear = () => {
		cancelRef.current();
		debounceInFlightRef.current = false;
		setInternalValue("");
		onValueChange?.("");
		onClear?.();
	};
	return /* @__PURE__ */ jsxs(InputGroup, {
		state,
		focusVariant,
		className,
		children: [
			/* @__PURE__ */ jsx(InputGroup.Addon, {
				align: "inline-start",
				children: /* @__PURE__ */ jsx(Search, { className: "tw:size-4 tw:shrink-0 tw:text-muted-foreground" })
			}),
			/* @__PURE__ */ jsx(InputGroup.Input, {
				...restInputProps,
				ref,
				value: internalValue,
				onChange: handleChange,
				onBlur: handleBlur,
				disabled,
				readOnly
			}),
			canClear && /* @__PURE__ */ jsx(InputGroup.Addon, {
				align: "inline-end",
				children: /* @__PURE__ */ jsx(InputGroup.Button, {
					"aria-label": "Clear search",
					onMouseDown: (event) => event.preventDefault(),
					onClick: handleClear,
					children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
				})
			})
		]
	});
};
export { SearchInput };

//# sourceMappingURL=SearchInput-BZmFTsMS.js.map